import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { isFirefox } from '../../utils/BrowserTest';
import { isModifierPressed } from '../../utils/DomEvents';
import { toShorthandSize } from '../../utils/propTypes/tshirtSize';
import { SIZE_OPTIONS, ALIGNMENT_CLASSES } from '../CheckboxConstants';
import { INPUT_FONT_SIZE, INPUT_SM_FONT_SIZE } from 'HubStyleTokens/sizes';
import { FLINT, KOALA } from 'HubStyleTokens/colors';
/** @internal */

const CHECKBOX_MIN_HEIGHT = '32px';

const getAlignItems = props => {
  if (props.stackContents) {
    return 'flex-start;';
  }

  return 'center';
};

const getToggleInputWrapperFontSize = props => {
  if (props.shorthandSize === 'sm') {
    return '92%';
  }

  return '1rem';
};

const ToggleInputWrapperDiv = styled.div.withConfig({
  displayName: "ToggleInputWrapper__ToggleInputWrapperDiv",
  componentId: "b0a6hp-0"
})(["align-items:", ";font-size:", ";display:flex;min-height:", ";position:relative;@media screen and (-ms-high-contrast:active),(-ms-high-contrast:none){&::after{content:' ';display:inline-block;min-height:", ";}}", ";", " ", ";", ";", ";"], props => getAlignItems(props), props => getToggleInputWrapperFontSize(props), CHECKBOX_MIN_HEIGHT, CHECKBOX_MIN_HEIGHT, props => props.stackContents && css(["flex-direction:column;&::after{display:none;}&", ",+ ", "{margin-top:16px;}&:first-child{margin-top:0;}"], ToggleInputWrapperDiv, ToggleInputWrapperDiv), props => props.shorthandSize === 'sm' && `margin-top: 0;`, props => props.readOnly && css([".private-checkbox__indicator{background-color:", " !important;border-color:", " !important;cursor:auto;color:", " !important;}"], KOALA, KOALA, FLINT), props => props.disabled && css([".private-checkbox__indicator:not(.private-radio__indicator){background-color:", ";color:", " !important;}"], KOALA, FLINT), props => props.inline && css(["display:inline-flex;min-height:", ";+ ", "{margin-left:1.25em;}"], CHECKBOX_MIN_HEIGHT, ToggleInputWrapperDiv));
const CheckboxLabel = styled.label.withConfig({
  displayName: "ToggleInputWrapper__CheckboxLabel",
  componentId: "b0a6hp-1"
})(["cursor:pointer;display:block;max-width:100%;", ";", ";&:hover .private-checkbox__indicator:not(.private-radio__indicator){background-color:", ";}&:hover .private-radio__circle{fill:", ";}"], props => props.disabled && 'cursor: not-allowed;', props => props.readOnly && 'cursor: default;', KOALA, KOALA);

const handleAlignmentType = alignment => {
  switch (alignment) {
    case 'baseline':
      return 'baseline';

    case 'center':
      return 'center';

    case 'end':
      return 'flex-end';

    default:
      return 'flex-start';
  }
};

const CheckboxInnerSpan = styled.span.withConfig({
  displayName: "ToggleInputWrapper__CheckboxInnerSpan",
  componentId: "b0a6hp-2"
})(["display:flex;align-items:", ";"], props => handleAlignmentType(props.alignment));
const CheckboxInnerTextSpan = styled.span.withConfig({
  displayName: "ToggleInputWrapper__CheckboxInnerTextSpan",
  componentId: "b0a6hp-3"
})(["position:relative;line-height:normal;max-width:100%;font-size:", ";padding-left:12px;top:0.0625em;", ";", ";.private-microcopy.is--text--help{margin-top:2px;}"], INPUT_FONT_SIZE, props => props.shorthandSize === 'sm' && css(["font-size:", ";padding-left:9px;top:auto;"], INPUT_SM_FONT_SIZE), props => props.disabled && `color: ${FLINT}`);

const getCheckboxDescPadLeft = props => {
  if (props.stackContents && props.shorthandSize === 'sm') {
    return 'padding-left: 25px;';
  }

  if (props.stackContents) {
    return 'padding-left: 32px;';
  }

  return '';
};

const CheckboxDescriptionSpan = styled.span.withConfig({
  displayName: "ToggleInputWrapper__CheckboxDescriptionSpan",
  componentId: "b0a6hp-4"
})(["margin-left:1ch;max-width:100%;position:relative;top:0.0625em;", ";", ";"], props => props.stackContents && `margin-left: 0;`, props => getCheckboxDescPadLeft(props));
const ToggleInputWrapper = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    alignment,
    checked,
    children,
    childWrapperId,
    className,
    description,
    descriptionId,
    descriptionLayout,
    disabled,
    indicator,
    inline,
    input,
    labelClassName,
    onChange,
    readOnly,
    size
  } = props,
        rest = _objectWithoutPropertiesLoose(props, ["alignment", "checked", "children", "childWrapperId", "className", "description", "descriptionId", "descriptionLayout", "disabled", "indicator", "inline", "input", "labelClassName", "onChange", "readOnly", "size"]);

  const shorthandSize = size && toShorthandSize(size);
  return /*#__PURE__*/_jsxs(ToggleInputWrapperDiv, Object.assign({}, rest, {
    className: classNames(className, SIZE_OPTIONS[shorthandSize]),
    disabled: disabled,
    inline: inline,
    readOnly: readOnly,
    shorthandSize: shorthandSize,
    stackContents: description && descriptionLayout === 'vertical',
    ref: ref,
    children: [/*#__PURE__*/_jsx(CheckboxLabel, {
      className: labelClassName,
      disabled: disabled,
      readOnly: readOnly,
      onClick: evt => {
        if (isFirefox() && isModifierPressed(evt) && onChange) {
          onChange(Object.assign({}, evt, {
            type: 'change',
            target: {
              checked: !checked
            }
          }));
        }
      },
      children: /*#__PURE__*/_jsxs(CheckboxInnerSpan, {
        alignment: alignment,
        children: [input, indicator, children && /*#__PURE__*/_jsx(CheckboxInnerTextSpan, {
          className: 'private-checkbox__text' + (shorthandSize === 'sm' ? " private-checkbox__text--small" : ""),
          shorthandSize: shorthandSize,
          disabled: disabled,
          id: childWrapperId,
          children: children
        })]
      })
    }), description && /*#__PURE__*/_jsx(CheckboxDescriptionSpan, {
      className: "private-checkbox__desc",
      shorthandSize: shorthandSize,
      stackContents: description && descriptionLayout === 'vertical',
      id: descriptionId,
      children: description
    })]
  }));
});
ToggleInputWrapper.displayName = 'ToggleInputWrapper';

if (process.env.NODE_ENV !== 'production') {
  ToggleInputWrapper.propTypes = {
    alignment: PropTypes.oneOf(Object.keys(ALIGNMENT_CLASSES)),
    checked: PropTypes.bool,
    children: PropTypes.node,
    childWrapperId: PropTypes.string,
    className: PropTypes.string,
    description: PropTypes.node,
    descriptionId: PropTypes.string.isRequired,
    descriptionLayout: PropTypes.string.isRequired,
    disabled: PropTypes.bool,
    indicator: PropTypes.node.isRequired,
    inline: PropTypes.bool,
    input: PropTypes.node.isRequired,
    labelClassName: PropTypes.string,
    onChange: PropTypes.func,
    readOnly: PropTypes.bool,
    size: PropTypes.string
  };
}

export default ToggleInputWrapper;